import React, { ChangeEvent, useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Label from "../../label";
import Textbox from "../../form/input";
import { Color } from "../../../constants/color";
import { ZIndex } from "../../../constants/z-index";
import { BreakpointsQuery } from "../../../constants/device";
import { UserPageResponse } from "../../../types";
import Button from "../../button/button";
import Suggerimento from "../../tooltip/suggerimento";
import { Icons } from "../../../constants/icons";
import { axiosInstance } from "../../../provider/authProvider";
import { avvisoUnico, messaggioDiErrore } from "../../../features/notifiche/avvisi";
import { nomeFileScaricato } from "../../../utils/nomeFileScaricato";
import DialogConferma from "../../modal/conferma";
import { BE_BASE_URL } from "../../../utils/config";
import { AxiosError, AxiosResponse } from "axios";
import { Trash2, Download, X, Eye } from "lucide-react";
import {
  SOLO_CIFRE_ATTRIBUTO,
  useSoloCifre,
} from "../../../features/form/soloCifre";

/*
 Un solo avviso per i documenti aziendali: caricamento, eliminazione ed errori si
 succedono sullo stesso posto invece di impilarsi.
*/
const AVVISO_DOCUMENTI = "azienda-documenti";

interface Props {
  edit: boolean;
  // Azienda in sola lettura (ODOO 300): la decide la pagina, che la legge dall'utenza.
  solaLettura?: boolean;
  companyData: UserPageResponse | undefined;
  editCompanyData: (
    field: string,
    event: ChangeEvent<HTMLInputElement>
  ) => void;
}

const Spacer = styled.div`
  content: "";
  height: 60px;
  border-radius: 18px;
  row-gap: 0.4rem;
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 80px;
  margin-top: 30px;
  input {
    background-color: ${Color.WHITE};
    box-shadow: none;
    border-style: solid;
    border-width: 2px;
    border-color: ${Color.LIGHT_BLUE};
    border-radius: 20px;
    width: 480px;
    height: 30px;
    @media ${BreakpointsQuery.tablet} {
      width: 400px;
    }
  }
  input:focus {
    outline-width: 0;
  }
  @media ${BreakpointsQuery.tablet} {
    column-gap: 20px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 30px;
`;
const UploadFilesContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: stretch;
  margin: 0 auto 2rem;
  width: 100%;
  gap: 3rem;
`;
const UploadDVRContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  width: 45%;
  padding: 1rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
`;

const UploadOrgChartContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  width: 45%;
  padding: 1rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
`;

interface NameContainerProps extends React.HTMLAttributes<HTMLSpanElement> {
  alt?: string;
}

const NameWrapper: React.FC<NameContainerProps> = ({
  alt,
  children,
  ...props
}) => (
  <NameContainer title={alt} {...props}>
    {children}
  </NameContainer>
);

const NameContainer = styled.span<NameContainerProps>`
  width: 33%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${Color.LIGHT_BLUE};
  font-weight: bold;
  font-family: inherit;
  font-size: 0.85rem;
`;

const DownloadDeleteContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
  gap: 1rem;
`;

interface FileData {
  // L'id di `CompanyDvrDto`, un Long nel backend: e' quello che `DELETE /company/dvr/{fileId}` vuole.
  id: number;
  fileName: string;
  uploadDate: string;
}

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${ZIndex.DIALOG};
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 2rem;
  width: 50%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid ${Color.LIGHT_BLUE};
  padding-bottom: 0.75rem;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: ${Color.BLUE};
  font-size: 1.2rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${Color.GREY};
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${Color.BLUE};
  }
`;

const FileList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FileItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${Color.LIGHT_GREY};

  &:last-child {
    border-bottom: none;
  }
`;

const FileName = styled.span`
  font-weight: bold;
  color: ${Color.BLUE};
  font-size: 1rem;
  display: block;
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 400px;
`;

const FileDate = styled.span`
  color: ${Color.GREY};
  font-size: 0.8rem;
`;

const FileActions = styled.div`
  display: flex;
  gap: 1rem;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &.download {
    color: ${Color.BLUE};
  }

  &.delete {
    color: ${Color.RED};
  }

  &:hover {
    opacity: 0.8;
  }
`;

const FilesCountBadge = styled.div`
  background-color: ${Color.BLUE};
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: bold;
  margin-left: 0.5rem;
`;

const FileCountDisplay = styled.div`
  display: flex;
  align-items: center;
  color: ${Color.LIGHT_BLUE};
  font-weight: bold;
  font-family: inherit;
  font-size: 0.85rem;
`;

const MainInfo = ({
  edit,
  companyData,
  editCompanyData,
  solaLettura = false,
}: Props) => {
  const { t } = useTranslation();
  // In sola lettura (ODOO 300) DVR e organigramma si scaricano, ma non si caricano né eliminano.
  const [dvrFile, setDvrFile] = useState<File | null>(null);
  const [orgChartFile, setOrgChartFile] = useState<File | null>(null);
  const dvrFileInputRef = useRef<HTMLInputElement | null>(null);
  const orgChartFileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDvrUploaded, setIsDvrUploaded] = useState(false);
  const [isOrgChartUploaded, setIsOrgChartUploaded] = useState(false);
  const [dvrFiles, setDvrFiles] = useState<FileData[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  /*
   Il documento di cui si e' chiesta l'eliminazione, in attesa della conferma: l'operazione
   non e' reversibile, quindi passa da una dialog dell'applicazione e non da window.confirm.
  */
  const [documentoDaEliminare, setDocumentoDaEliminare] = useState<{
    docType: string;
    fileId?: number;
  } | null>(null);
  /*
   La partita IVA e' fatta di sole cifre e la sua casella e' qui dentro: l'ascolto sta sul
   contenitore e riguarda la sola casella marcata. Vedi `features/form/soloCifre.ts`.
  */
  const campiSoloCifre = useSoloCifre<HTMLDivElement>();

  useEffect(() => {
    fetchDVRFiles();
    fetchOrgChartName();
  }, []);

  const handleDvrFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    if (target.files) {
      const uploadedFile = target.files[0];
      setDvrFile(uploadedFile);

      // Carica automaticamente il file quando selezionato
      if (uploadedFile) {
        const formData = new FormData();
        formData.append("file", uploadedFile);
        formData.append("docType", "dvr");

        axiosInstance
          .patch(`${BE_BASE_URL}/company`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(() => {
            avvisoUnico(AVVISO_DOCUMENTI, "success", "Documento caricato con successo");
            fetchDVRFiles();
            setDvrFile(null);
            if (dvrFileInputRef.current) {
              dvrFileInputRef.current.value = "";
            }
          })
          .catch((error) => {
            const axiosError = error as AxiosError;
            let errorMessage = "Errore durante il salvataggio del file.";
            if (axiosError.response) {
              const data = axiosError.response.data as ErrorResponse;
              if (data && data.message) {
                errorMessage = data.message;
              }
            } else if (axiosError.message) {
              errorMessage = axiosError.message;
            }
            avvisoUnico(AVVISO_DOCUMENTI, "error", errorMessage);
          });
      }
    }
  };

  const handleOrgChartFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    if (target.files) {
      const uploadedFile = target.files[0];
      setOrgChartFile(uploadedFile);

      // Carica automaticamente il file quando selezionato
      if (uploadedFile) {
        const formData = new FormData();
        formData.append("file", uploadedFile);
        formData.append("docType", "orgChart");

        axiosInstance
          .patch(`${BE_BASE_URL}/company`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(() => {
            avvisoUnico(AVVISO_DOCUMENTI, "success", "Documento caricato con successo");
            fetchOrgChartName();
          })
          .catch((error) => {
            const axiosError = error as AxiosError;
            let errorMessage = "Errore durante il salvataggio del file.";
            if (axiosError.response) {
              const data = axiosError.response.data as ErrorResponse;
              if (data && data.message) {
                errorMessage = data.message;
              }
            } else if (axiosError.message) {
              errorMessage = axiosError.message;
            }
            avvisoUnico(AVVISO_DOCUMENTI, "error", errorMessage);
          });
      }
    }
  };

  const handleDvrClickRef = () => {
    if (dvrFileInputRef.current) {
      dvrFileInputRef.current.click();
    }
  };

  const handleOrgChartClickRef = () => {
    if (orgChartFileInputRef.current) {
      orgChartFileInputRef.current.click();
    }
  };

  interface ErrorResponse {
    message?: string;
  }

  const handleFileProcess = async (
    event: React.MouseEvent<HTMLButtonElement>,
    docType: string
  ) => {
    event.preventDefault();

    let fileToUpload: File | null = null;

    // Determina quale file deve essere processato e quale endpoint deve essere usato
    if (docType === "dvr") {
      fileToUpload = dvrFile;
    } else if (docType === "orgChart") {
      fileToUpload = orgChartFile;
    } else {
      avvisoUnico(AVVISO_DOCUMENTI, "error", "Tipo di documento non valido.");
      return;
    }

    if (fileToUpload) {
      try {
        const formData = new FormData();
        formData.append("file", fileToUpload);
        formData.append("docType", docType);
        await axiosInstance.patch(`${BE_BASE_URL}/company`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        avvisoUnico(AVVISO_DOCUMENTI, "success", "Documento caricato con successo");

        // Aggiorna lo stato dopo il caricamento
        if (docType === "dvr") {
          fetchDVRFiles();
          setDvrFile(null);
          if (dvrFileInputRef.current) {
            dvrFileInputRef.current.value = "";
          }
        } else if (docType === "orgChart") {
          fetchOrgChartName();
        }
      } catch (error) {
        const axiosError = error as AxiosError;
        let errorMessage = "Errore durante il salvataggio del file.";
        if (axiosError.response) {
          const data = axiosError.response.data as ErrorResponse;
          if (data && data.message) {
            errorMessage = data.message;
          }
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
        avvisoUnico(AVVISO_DOCUMENTI, "error", errorMessage);
      }
    } else {
      avvisoUnico(AVVISO_DOCUMENTI, "warning", "Nessun file selezionato.");
    }
  };

  // Aggiornare il metodo per recuperare tutti i file DVR
  const fetchDVRFiles = async () => {
    try {
      const response = await axiosInstance.get(
        `${BE_BASE_URL}/company/dvr-files`
      );
      if (response.status === 200) {
        //console.log("File DVR ricevuti:", response.data); // Log per debug
        setDvrFiles(response.data);
        setIsDvrUploaded(response.data.length > 0);
      }
    } catch (e) {
      const error = e as AxiosError;
      //console.error("Errore durante il recupero dei file DVR:", error);
    }
  };

  const fetchOrgChartName = async () => {
    try {
      const axiosResponse = await axiosInstance.get(
        `${BE_BASE_URL}/company/orgChart-name`
      );
      if (axiosResponse.status === 200) {
        setIsOrgChartUploaded(true);
        const fileName = axiosResponse.data;
        setOrgChartFile({ name: fileName } as File);
      }
    } catch (e) {
      avvisoUnico(
        AVVISO_DOCUMENTI,
        "error",
        messaggioDiErrore(e, "Errore durante il recupero dei documenti.")
      );
    }
  };

  // Aggiorna il metodo per scaricare un file DVR specifico
  const handleDownload = async (docType: string, fileId?: number) => {
    try {
      let url: string;
      if (docType === "dvr") {
        url = fileId
          ? `${BE_BASE_URL}/company/download?docType=dvr&fileId=${fileId}`
          : `${BE_BASE_URL}/company/download?docType=dvr`;
      } else if (docType === "orgChart") {
        url = `${BE_BASE_URL}/company/download?docType=orgChart`;
      } else {
        throw new Error("Invalid document type");
      }

      const response = await axiosInstance({
        url: url,
        method: "GET",
        responseType: "blob",
      });

      // Il nome esatto, accenti compresi, sta in `filename*`: vedi nomeFileScaricato.
      const filename = nomeFileScaricato(
        response.headers["content-disposition"],
        docType === "dvr" ? "dvr" : "organigramma"
      );

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      //console.error("Errore durante il download del documento:", error);
    }
  };

  // Aggiorna il metodo per eliminare un file DVR specifico
  const eliminaDocumento = async (docType: string, fileId?: number) => {
    let url: string;
    if (docType === "dvr") {
      /*
       I DVR sono piu' d'uno e il backend li elimina solo uno alla volta, per id:
       `DELETE /company/dvr/{fileId}`. Un `DELETE /company/dvr` senza id, residuo di quando il
       DVR era un file solo, non esiste piu' e finiva in errore: senza id non si chiama nulla.
      */
      if (fileId == null) {
        avvisoUnico(AVVISO_DOCUMENTI, "error", t("user-dvr-non-identificato"));
        return;
      }
      url = `${BE_BASE_URL}/company/dvr/${fileId}`;
    } else if (docType === "orgChart") {
      url = `${BE_BASE_URL}/company/orgChart`;
    } else {
      avvisoUnico(AVVISO_DOCUMENTI, "error", "Tipo di documento non valido.");
      return;
    }

    try {
      await axiosInstance.delete(url);
      if (docType === "dvr") {
        // Aggiorna l'elenco di file dopo l'eliminazione
        fetchDVRFiles();
      } else if (docType === "orgChart") {
        setIsOrgChartUploaded(false);
        setOrgChartFile(null);
        if (orgChartFileInputRef.current) {
          orgChartFileInputRef.current.value = "";
        }
      }
      avvisoUnico(AVVISO_DOCUMENTI, "success", "Documento eliminato con successo.");
    } catch (e) {
      avvisoUnico(
        AVVISO_DOCUMENTI,
        "error",
        messaggioDiErrore(e, "Errore durante l'eliminazione del documento.")
      );
    }
  };

  const confermaEliminazione = async () => {
    if (!documentoDaEliminare) return;
    const { docType, fileId } = documentoDaEliminare;
    setDocumentoDaEliminare(null);
    await eliminaDocumento(docType, fileId);
  };

  const openModal = () => {
    fetchDVRFiles(); // Assicuriamoci di avere l'elenco aggiornato
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Formatta la data per la visualizzazione
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <MainContainer>
      <Label
        text="Documenti aziendali"
        fontSize={"26px"}
        fontWeight={"bold"}
        textTransform={"uppercase"}
      />
      <UploadFilesContainer>
        <UploadDVRContainer>
          <Label
            text={t("user-main-info-dvr-label")}
            style={{ width: "35%" }}
          ></Label>

          {/* Mostra il conteggio dei file invece del nome del file */}
          {dvrFiles.length > 0 ? (
            <FileCountDisplay>
              <span>File DVR caricati</span>
              <FilesCountBadge>{dvrFiles.length}</FilesCountBadge>
              <Suggerimento testo={t("user-aiuto-visualizza-dvr")}>
                <ActionButton
                  aria-label="Visualizza i file DVR"
                  onClick={openModal}
                >
                  <Eye
                    size={20}
                    style={{ color: Color.BLUE, marginLeft: "10px" }}
                  />
                </ActionButton>
              </Suggerimento>
            </FileCountDisplay>
          ) : (
            <NameWrapper>Nessun file DVR caricato</NameWrapper>
          )}

          <Suggerimento
            testo={t(solaLettura ? "sola-lettura-comando" : "user-aiuto-carica-dvr")}
            style={{ width: "25%" }}
          >
            <Button
              id="file-upload"
              label={t("user-main-info-upload-button")}
              color={Color.BLUE}
              icon={Icons.BUTTONS.UPLOAD}
              style={{ width: "100%" }}
              onClick={handleDvrClickRef}
              disabled={solaLettura}
            />
          </Suggerimento>

          <input
            id="file-upload"
            type="file"
            ref={dvrFileInputRef}
            style={{ display: "none" }}
            onChange={handleDvrFileUpload}
          />
        </UploadDVRContainer>

        <UploadOrgChartContainer>
          <Label
            text={t("user-main-info-org-chart-label")}
            style={{ width: "25%" }}
          ></Label>
          <NameWrapper alt={orgChartFile?.name}>
            {orgChartFile?.name || "Nessun file caricato"}
          </NameWrapper>

          <Suggerimento
            testo={t(solaLettura ? "sola-lettura-comando" : "user-aiuto-carica-organigramma")}
            style={{ width: "25%" }}
          >
            <Button
              label={t("user-main-info-upload-button")}
              color={Color.BLUE}
              icon={Icons.BUTTONS.UPLOAD}
              style={{ width: "100%" }}
              onClick={handleOrgChartClickRef}
              disabled={solaLettura}
            />
          </Suggerimento>

          <input
            id="file-upload"
            type="file"
            ref={orgChartFileInputRef}
            style={{ display: "none" }}
            onChange={handleOrgChartFileUpload}
          />

          <DownloadDeleteContainer>
            {isOrgChartUploaded && (
              <button
                aria-label="Scarica l'organigramma"
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "blue",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  justifyContent: "center",
                  flexShrink: "0",
                }}
                onClick={() => handleDownload("orgChart")}
              >
                <Download width={22} height={22} />
              </button>
            )}
            {isOrgChartUploaded && !solaLettura && (
              <button
                aria-label="Elimina l'organigramma"
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "red",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  justifyContent: "center",
                  flexShrink: "0",
                }}
                onClick={() => setDocumentoDaEliminare({ docType: "orgChart" })}
              >
                <Trash2 width={22} height={22} />
              </button>
            )}
          </DownloadDeleteContainer>
        </UploadOrgChartContainer>
      </UploadFilesContainer>

      {/* Resto del componente... */}
      <Label
        text={t("user-main-info-label")}
        fontSize={"26px"}
        fontWeight={"bold"}
        textTransform={"uppercase"}
      />
      <InfoContainer ref={campiSoloCifre}>
        <Column>
          <Textbox
            label={t("user-business-name")}
            alt={t("user-business-name")}
            autocomplete={"off"}
            form={"company-info"}
            name={t("user-business-name")}
            defaultValue={companyData ? companyData.businessName : ""}
            type={"text"}
            textAlign={"left"}
            onChange={(e) => {
              editCompanyData("businessName", e);
            }}
            required={false}
            edit={false}
          />
          <Textbox
            label={t("user-contact-first-name")}
            alt={t("user-contact-first-name")}
            autocomplete={"off"}
            form={"company-info"}
            name={t("user-contact-first-name")}
            defaultValue={companyData ? companyData.adminFirstName : ""}
            type={"text"}
            textAlign={"left"}
            onChange={(e) => {
              editCompanyData("adminFirstName", e);
            }}
            required={false}
            edit={false}
          />
          <Textbox
            label={t("user-contact-phone")}
            alt={t("user-contact-phone")}
            autocomplete={"off"}
            form={"company-info"}
            name={t("user-contact-phone")}
            defaultValue={companyData ? companyData.adminPhone : ""}
            type={"text"}
            textAlign={"left"}
            onChange={(e) => {
              if (e.target.value.length <= 10) {
                editCompanyData("adminPhone", e);
              }
            }}
            required={false}
            edit={edit}
            maxLength={10}
          />
          <Textbox
            label={t("user-sdi-code")}
            alt={t("user-sdi-code")}
            autocomplete={"off"}
            form={"company-info"}
            name={t("user-sdi-code")}
            defaultValue={companyData ? companyData.companyInfo.sdiCode : ""}
            type={"text"}
            textAlign={"left"}
            onChange={(e) => {
              editCompanyData("companyInfo.sdiCode", e);
            }}
            required={false}
            edit={false}
          />
        </Column>
        <Column>
          <Textbox
            label={t("user-fiscal-code")}
            alt={t("user-fiscal-code")}
            autocomplete={"off"}
            form={"company-info"}
            name={t("user-fiscal-code")}
            defaultValue={companyData ? companyData.vatNumber : ""}
            type={"text"}
            textAlign={"left"}
            onChange={(e) => {
              // Undici caratteri e non sedici: la colonna `company.vat_number` e' varchar(11), e
              // un codice fiscale digitato per intero si perderebbe troncato nel salvataggio.
              if (e.target.value.length <= 11) {
                editCompanyData("vatNumber", e);
              }
            }}
            required={false}
            edit={edit}
            /*
             Il tetto delle undici cifre vive su tre piani, e nessuno dei tre sostituisce gli
             altri. `maxLength` ferma la dodicesima cifra, battuta o incollata che sia, perche'
             il browser tronca ogni modifica che sfonderebbe il limite. L'attributo qui sotto
             chiude l'altra meta' della porta: le lettere. La validazione al salvataggio
             (`partitaIvaOCodiceFiscaleValido`) e il `@Pattern` del backend restano: chi arriva
             per altra via continua a trovare il rifiuto.

             Sole cifre e non alfanumerico: la colonna e' `varchar(11)`, quindi il codice fiscale
             di sedici caratteri non ci e' mai potuto entrare, e il backend accetta oggi soltanto
             undici cifre ("La partita IVA deve essere composta da 11 cifre"). Un valore di undici
             caratteri con dentro una lettera non sarebbe stato accettato da nessuno dei due:
             impedirlo qui evita di farlo scrivere per poi rifiutarlo.
            */
            maxLength={11}
            inputMode="numeric"
            {...{ [SOLO_CIFRE_ATTRIBUTO]: true }}
          />
          <Textbox
            label={t("user-contact-last-name")}
            alt={t("user-contact-last-name")}
            autocomplete={"off"}
            form={"company-info"}
            name={t("user-contact-last-name")}
            defaultValue={companyData ? companyData.adminLastName : ""}
            type={"text"}
            textAlign={"left"}
            onChange={(e) => {
              editCompanyData("adminLastName", e);
            }}
            required={false}
            edit={false}
          />
          <Textbox
            label={t("user-email")}
            alt={t("user-email")}
            autocomplete={"off"}
            form={"company-info"}
            name={t("user-email")}
            defaultValue={companyData ? companyData.adminEmail : ""}
            type={"text"}
            textAlign={"left"}
            onChange={(e) => {
              editCompanyData("adminEmail", e);
            }}
            required={false}
            edit={false}
          />
          <Textbox
            label={t("user-cem")}
            alt={t("user-cem")}
            autocomplete={"off"}
            form={"company-info"}
            name={t("user-cem")}
            defaultValue={companyData ? companyData.cem : ""}
            type={"text"}
            textAlign={"left"}
            onChange={(e) => {
              editCompanyData("cem", e);
            }}
            required={false}
            edit={false}
          />
        </Column>
      </InfoContainer>

      {/* Modal per visualizzare e gestire i file DVR */}
      {isModalOpen && (
        <ModalBackdrop>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>File DVR</ModalTitle>
              <CloseButton
                aria-label={t("user-chiudi-file-dvr")}
                onClick={closeModal}
              >
                <X size={24} />
              </CloseButton>
            </ModalHeader>
            <FileList>
              {dvrFiles.length > 0 ? (
                dvrFiles.map((file) => (
                  <FileItem key={file.id}>
                    <div>
                      <FileName>
                        {file.fileName || "Nome file non disponibile"}
                      </FileName>
                      <FileDate>
                        Caricato il:{" "}
                        {file.uploadDate
                          ? formatDate(file.uploadDate)
                          : "Data non disponibile"}
                      </FileDate>
                    </div>
                    <FileActions>
                      <ActionButton
                        className="download"
                        onClick={() => handleDownload("dvr", file.id)}
                        title="Download"
                      >
                        <Download size={20} />
                      </ActionButton>
                      {!solaLettura && (
                      <ActionButton
                        className="delete"
                        onClick={() =>
                          setDocumentoDaEliminare({
                            docType: "dvr",
                            fileId: file.id,
                          })
                        }
                        title="Elimina"
                      >
                        <Trash2 size={20} />
                      </ActionButton>
                      )}
                    </FileActions>
                  </FileItem>
                ))
              ) : (
                <div style={{ textAlign: "center", padding: "1rem" }}>
                  Nessun file DVR caricato
                </div>
              )}
            </FileList>
            <div style={{ marginTop: "1rem", textAlign: "center" }}>
              <Button
                label="Carica nuovo file"
                color={Color.BLUE}
                icon={Icons.BUTTONS.UPLOAD}
                onClick={handleDvrClickRef}
                disabled={solaLettura}
              />
            </div>
          </ModalContent>
        </ModalBackdrop>
      )}
      <DialogConferma
        aperta={documentoDaEliminare !== null}
        titolo="Elimina documento"
        messaggio="Sei sicuro di voler eliminare il documento?"
        etichettaConferma="ELIMINA"
        coloreConferma={Color.RED}
        coloreAnnulla={Color.BLUE}
        onConferma={confermaEliminazione}
        onAnnulla={() => setDocumentoDaEliminare(null)}
      />
    </MainContainer>
  );
};

export default MainInfo;
